import { Link } from 'react-router'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'

import { listPermissions, listRequests } from '../../api/portal'
import { DataState } from '../../components/DataState'
import { Money } from '../../components/Money'
import { StatusChip } from '../../components/ui/StatusChip'
import { UNSET, formatDateTime } from '../../format'

/**
 * What this application may call, and what it has actually called.
 *
 * Both were reachable only from other screens: access lived on the services
 * page and usage on the requests page, each listing every application at once.
 * Somebody looking at one application had to filter two other pages by hand to
 * answer "what can this key do, and is it working?" — which is the first
 * question anyone opens an application to ask.
 *
 * Access is read-only here on purpose. A customer cannot grant themselves an
 * API; the list says who to ask instead of offering a control that would be
 * refused.
 */
export function ApplicationAccess({
  customerId,
  applicationId,
}: {
  customerId: string
  applicationId: string
}) {
  const permissions = useQuery({
    queryKey: ['client', 'permissions', customerId],
    queryFn: ({ signal }) => listPermissions(customerId, signal),
  })

  const granted = (permissions.data?.results ?? []).filter(
    (permission) => permission.application === applicationId,
  )

  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
      <Typography variant="h2" gutterBottom>
        سرویس‌های در دسترس این اپلیکیشن
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        دسترسی‌ها را ما فعال می‌کنیم؛ از سمت شما قابل افزودن نیست. برای دسترسی تازه با ما تماس
        بگیرید.
      </Typography>
      <DataState
        isPending={permissions.isPending}
        isError={permissions.isError}
        error={permissions.error}
        onRetry={() => void permissions.refetch()}
      >
        {granted.length === 0 ? (
          <Typography variant="body2">هنوز هیچ سرویسی به این اپلیکیشن داده نشده است.</Typography>
        ) : (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {granted.map((permission) => (
              <Chip
                key={permission.id}
                label={`${permission.display_name_fa || permission.operation_name}`}
                variant="outlined"
                color={permission.is_enabled ? 'success' : 'default'}
              />
            ))}
          </Stack>
        )}
      </DataState>
    </Paper>
  )
}

export function ApplicationRequests({
  customerId,
  applicationId,
}: {
  customerId: string
  applicationId: string
}) {
  const requests = useQuery({
    queryKey: ['client', 'requests', customerId, 'application', applicationId],
    queryFn: ({ signal }) => listRequests(customerId, 1, { application: applicationId }, signal),
  })
  const rows = (requests.data?.results ?? []).slice(0, 10)

  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ justifyContent: 'space-between', alignItems: { sm: 'baseline' }, mb: 2 }}
      >
        <Typography variant="h2">آخرین فراخوانی‌های این اپلیکیشن</Typography>
        <Typography
          component={Link}
          to="/requests"
          variant="body2"
          sx={{ color: 'primary.main', textDecoration: 'none' }}
        >
          همهٔ درخواست‌ها
        </Typography>
      </Stack>
      <DataState
        isPending={requests.isPending}
        isError={requests.isError}
        error={requests.error}
        onRetry={() => void requests.refetch()}
      >
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            این اپلیکیشن هنوز فراخوانی‌ای انجام نداده است.
          </Typography>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>سرویس</TableCell>
                  <TableCell>وضعیت</TableCell>
                  <TableCell align="left">هزینه</TableCell>
                  <TableCell>زمان</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.request_id} hover>
                    <TableCell>
                      <Typography variant="body2">{row.operation_code}</Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={row.status} />
                    </TableCell>
                    <TableCell align="left">
                      <Money amountIrr={row.charged_amount_irr} whenUnknown={UNSET.amount} />
                    </TableCell>
                    <TableCell>
                      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(row.created_at)}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataState>
    </Paper>
  )
}
